import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal, WritableSignal } from '@angular/core';

import { Router, RouterLink } from '@angular/router';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { ContentfulArticleContent } from 'src/app/core/interfaces/Contentful/Article/ContentfulArticleContent';
import { ContentfulSectionContent } from 'src/app/core/interfaces/Contentful/ContentfulSectionContent';
import { MiniNavigationContent } from 'src/app/core/interfaces/Contentful/MiniNavigation/MiniNavigationContent';
import { ContentfulRichBlockContent } from 'src/app/core/interfaces/Contentful/RichBlock';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-contentful-mini-navigation',
  templateUrl: './contentful-mini-navigation.component.html',
  styleUrls: ['./contentful-mini-navigation.component.scss'],
  standalone: true,
  imports: [RouterLink, NgClass],
})
export class AppContentfulMiniNavigationComponent implements OnInit {
  @Input() contentfulSectionContent: ContentfulSectionContent | undefined;

  currentUrl: string;
  miniNavigation: WritableSignal<MiniNavigationContent[] | undefined> = signal(undefined);
  dropdownOpen = signal(false);
  #logger = inject(GenericLogger);

  constructor(private router: Router) {
    this.currentUrl = this.router.url.split('#')[0];
  }

  ngOnInit() {
    if (this.contentfulSectionContent) {
      // @TODO: Check how we can differ the different contentful content types without using this any
      const contentfulSectionContent = this.contentfulSectionContent.contentfulContent?.content as ContentfulArticleContent;

      // Hide mini-navigation for single header (done in the original too)
      if (contentfulSectionContent?.contents && contentfulSectionContent?.contents.length > 1) {
        this.miniNavigation.set(this.transformRichBlockContentIntoMiniNavigation(contentfulSectionContent.contents));
      }
    }
  }

  toggleDropdown() {
    this.dropdownOpen.update((isOpen) => !isOpen);
  }

  transformRichBlockContentIntoMiniNavigation(
    contentRichBlock: ContentfulRichBlockContent[],
  ): MiniNavigationContent[] | undefined {
    // Avoid adding data to miniNavigation if there is no anchorId
    const contentWithAnchorId = contentRichBlock.filter(({ anchorId }) => anchorId !== undefined);

    if (contentWithAnchorId.length === 0) {
      return undefined;
    }

    const miniNavigation = contentWithAnchorId
      .map(({ anchorId, content }) => {
        const heading2 = content?.find(({ nodeType }) => nodeType === 'heading-2');
        const heading3 = content?.find(({ nodeType }) => nodeType === 'heading-3');
        const title = heading2?.content?.[0]?.value ?? heading3?.content?.[0]?.value ?? null;

        if (!title) {
          this.#logger.warn(
            `No title found for the anchorId: ${anchorId} in the mini-navigation of the page: ${this.currentUrl}`,
          );
          return null;
        }

        return { anchorId, title };
      })
      .filter((content) => content !== null);

    return miniNavigation.length ? miniNavigation : undefined;
  }
}
